<template>
  <Drawer
    v-model:visible="isVisible"
    position="right"
    :style="{ width: 'min(32rem, 100%)' }"
    :show-close-icon="false"
    :pt-options="{ mergeProps: true }"
    :pt="{ content: { class: 'px-6' } }"
    @hide="handleClose"
  >
    <template #header>
      <div class="w-full flex justify-end gap-2 items-center">
        <PaymentActionsMenu
          v-if="payment"
          :payment="payment"
          :subscriber-membership-id="getSubscriberMembershipId(payment)"
          @action-success="handleActionSuccess"
          @refund="handleRefund"
        />
        <button
          class="p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
          @click="isVisible = false"
        >
          <i class="pi pi-times text-xl" />
        </button>
      </div>
    </template>

    <div class="mb-6">
      <h2 class="text-xl font-semibold">{{ paymentTitle }}</h2>
      <div class="text-2xl font-bold mt-2">
        {{ getFormattedCurrency(payment?.amount || 0) }}
      </div>
      <div
        v-if="payment?.fee_amount && payment.fee_amount > 0"
        class="text-sm text-gray-500 mt-1"
      >
        Fee: {{ getFormattedCurrency(payment.fee_amount) }}
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center p-8">
      <ProgressSpinner />
    </div>

    <div v-else-if="fetchError" class="p-4">
      <Message severity="error">Failed to load payment details</Message>
    </div>

    <div v-else-if="payment" class="space-y-6">
      <div class="grid grid-cols-[auto_1fr] gap-x-16 gap-y-4">
        <div class="font-semibold">Paid by</div>
        <div class="text-gray-700">{{ payment.paid_by || "N/A" }}</div>

        <template v-if="payment.player_name">
          <div class="font-semibold">Player</div>
          <div class="text-gray-700">{{ payment.player_name }}</div>
        </template>

        <div class="font-semibold">Source</div>
        <div
          v-if="sourceRoute"
          class="text-blue-600 hover:text-blue-800 cursor-pointer underline"
          @click="navigateToSource"
        >
          {{ payment.source_name || "N/A" }}
        </div>
        <div v-else class="text-gray-700">
          {{ payment.source_name || "N/A" }}
        </div>

        <div class="font-semibold">Method</div>
        <div class="text-gray-700">
          {{ formatPaymentMethod(payment.payment_method) }}
        </div>

        <template v-if="payment.status === PaymentsLogStatusEnum.SCHEDULED">
          <div class="font-semibold">Due Date</div>
          <div class="text-gray-700">
            {{ getFormattedDate(payment.scheduled_at) }}
          </div>
        </template>

        <div class="font-semibold">Payment Status</div>
        <div class="flex items-center gap-2 whitespace-nowrap">
          <i
            :class="`pi pi-circle-fill text-xs ${getStatusColor(payment)}`"
          ></i>
          <span class="text-gray-700">{{ formatStatus(payment.status) }}</span>
        </div>
      </div>

      <div
        v-if="payment.decline_reason"
        class="rounded-lg border border-red-200 bg-red-50 p-4"
      >
        <div class="flex items-start gap-3">
          <i class="pi pi-exclamation-circle text-red-500 mt-0.5"></i>
          <div>
            <div class="font-semibold text-red-800">
              {{ payment.decline_reason }}
            </div>
            <div
              v-if="payment.decline_action"
              class="text-sm text-red-700 mt-1"
            >
              {{ payment.decline_action }}
            </div>
          </div>
        </div>
      </div>

      <div class="-mx-6">
        <PaymentActivityFeed
          :entries="scheduledPaymentDetail?.activity_feed ?? []"
          :is-loading="isLoadingScheduledPaymentDetail"
          :error="scheduledPaymentDetailError"
        />
      </div>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { useRouterNavigation } from "@/composables/useRouterNavigation";
import Drawer from "primevue/drawer";
import ProgressSpinner from "primevue/progressspinner";
import Message from "primevue/message";
import PaymentActionsMenu from "./PaymentActionsMenu.vue";
import PaymentActivityFeed from "./PaymentActivityFeed.vue";
import { usePaymentDetail } from "../composables/usePaymentDetail";
import { useScheduledPaymentDetail } from "../composables/useScheduledPaymentDetail";
import { getFormattedCurrency, getFormattedDate } from "@/shared/utils/helpers";
import { PaymentsLogStatusEnum, type PaymentsLog } from "@/api/model";
import {
  formatPaymentMethod,
  getBookingSourceId,
  getPaymentSourceRoute,
  getSubscriberMembershipId,
} from "@/modules/payments/helpers";
import {
  PAYMENT_STATUS_COLOR_SUCCESS,
  PAYMENT_STATUS_COLOR_PENDING,
  PAYMENT_STATUS_COLOR_ERROR,
  PAYMENT_STATUS_COLOR_NEUTRAL,
  PAYMENT_STATUS_COLOR_DEFAULT,
} from "@/modules/payments/constants";

interface Props {
  paymentId: string | null;
  hasFailedPayment?: boolean;
  // When true, a booking-session Source emits `open-booking-detail` so the host
  // can open the Booking Detail Drawer instead of routing to the old full-page
  // booking screen. Off by default so contexts that don't host that drawer keep
  // their existing navigation.
  bookingSourceOpensDrawer?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  hasFailedPayment: false,
  bookingSourceOpensDrawer: false,
});

const emit = defineEmits<{
  "action-success": [];
  refund: [payment: PaymentsLog];
  "open-booking-detail": [bookedServiceId: string];
}>();

const isVisible = defineModel<boolean>("visible", { required: true });

const { navigateWithQuery } = useRouterNavigation();

const { paymentDetail, isLoading, fetchError, fetchPaymentById } =
  usePaymentDetail();

const {
  detail: scheduledPaymentDetail,
  isLoading: isLoadingScheduledPaymentDetail,
  error: scheduledPaymentDetailError,
  fetchDetail: fetchScheduledPaymentDetail,
} = useScheduledPaymentDetail();

const payment = computed(() => paymentDetail.value);

const paymentTitle = computed(() => {
  if (!payment.value) return "Payment";

  const type = payment.value.type || "Payment";
  let title = `${type} Payment`;

  const supportsInstallments =
    payment.value.type?.toLowerCase() !== "membership";

  if (
    supportsInstallments &&
    payment.value.installment_position &&
    payment.value.total_installments
  ) {
    title += ` ${payment.value.installment_position} of ${payment.value.total_installments}`;
  }

  return title;
});

const sourceRoute = computed(() => {
  if (!payment.value) {
    return null;
  }
  return getPaymentSourceRoute(payment.value);
});

const navigateToSource = () => {
  // A booking-session source opens the new Booking Detail Drawer instead of the
  // old full-page route, but only where the host opts in (it owns that drawer).
  // The booking drawer itself embeds this payment drawer, so importing it here
  // would be circular — we emit up instead.
  const bookingSourceId = payment.value
    ? getBookingSourceId(payment.value)
    : null;
  if (bookingSourceId && props.bookingSourceOpensDrawer) {
    emit("open-booking-detail", bookingSourceId);
    return;
  }
  if (sourceRoute.value) {
    navigateWithQuery(sourceRoute.value);
  }
};

watch(
  [() => props.paymentId, isVisible],
  ([newPaymentId, visible]) => {
    if (newPaymentId && visible) {
      fetchPaymentById(newPaymentId);
      fetchScheduledPaymentDetail(newPaymentId);
    }
  },
  { immediate: true }
);

const handleClose = () => {
  isVisible.value = false;
};

const toTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .split("_")
    .map(function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const isPaymentPastDue = (payment: any): boolean => {
  if (payment.status !== PaymentsLogStatusEnum.SCHEDULED) return false;
  const scheduledDate = new Date(payment.scheduled_at);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return scheduledDate < today;
};

const formatStatus = (status: string) => {
  if (status === PaymentsLogStatusEnum.SCHEDULED && payment.value) {
    if (isPaymentPastDue(payment.value)) {
      return "Past Due";
    }
    if (props.hasFailedPayment) {
      return "Paused";
    }
  }
  return toTitleCase(status);
};

const getStatusColor = (paymentData: any) => {
  if (paymentData.status === PaymentsLogStatusEnum.PAID) {
    return PAYMENT_STATUS_COLOR_SUCCESS;
  } else if (paymentData.status === PaymentsLogStatusEnum.REFUNDED) {
    return PAYMENT_STATUS_COLOR_NEUTRAL;
  } else if (
    paymentData.status === PaymentsLogStatusEnum.PAST_DUE ||
    paymentData.status === PaymentsLogStatusEnum.FAILED
  ) {
    return PAYMENT_STATUS_COLOR_ERROR;
  } else if (paymentData.status === PaymentsLogStatusEnum.SCHEDULED) {
    if (isPaymentPastDue(paymentData)) {
      return PAYMENT_STATUS_COLOR_ERROR;
    }
    if (props.hasFailedPayment) {
      return PAYMENT_STATUS_COLOR_NEUTRAL;
    }
    return PAYMENT_STATUS_COLOR_PENDING;
  } else if (paymentData.status === PaymentsLogStatusEnum.PROCESSING) {
    return PAYMENT_STATUS_COLOR_PENDING;
  } else if (paymentData.status === PaymentsLogStatusEnum.REVERSED) {
    return PAYMENT_STATUS_COLOR_NEUTRAL;
  }

  return PAYMENT_STATUS_COLOR_DEFAULT;
};

const handleActionSuccess = () => {
  if (props.paymentId) {
    fetchPaymentById(props.paymentId);
    fetchScheduledPaymentDetail(props.paymentId);
  }
  emit("action-success");
};

const handleRefund = (payment: PaymentsLog) => {
  emit("refund", payment);
};
</script>
